<template>
  <div
    class="max-w-full overflow-x-auto text-sm text-obsidian-900"
    data-testid="ledger-cost-breakdown"
  >
    <div
      class="inline-grid min-w-min items-center gap-x-4 gap-y-1"
      :style="{ gridTemplateColumns: gridTemplateColumns }"
    >
      <div />
      <div
        v-for="counterpartyType in visibleCounterpartyTypes"
        :key="`header-${counterpartyType}`"
        class="text-right text-obsidian-500"
      >
        <template v-if="visibleCounterpartyTypes.length > 1">
          {{ t(`counterpartyTypes.${counterpartyType}`) }}
        </template>
      </div>

      <template v-for="row in costRows" :key="row.key">
        <div class="py-0.5 text-right text-obsidian-500">
          {{ row.label }}
          <span v-if="row.fromQuotedPrice" class="text-obsidian-400">{{ t('quoted') }}</span>
        </div>
        <div
          v-for="counterpartyType in visibleCounterpartyTypes"
          :key="`${row.key}-${counterpartyType}`"
          class="py-0.5 text-right tabular-nums"
        >
          <span
            v-if="row.amounts[counterpartyType] !== undefined"
            v-currency="row.amounts[counterpartyType]"
          />
          <span v-else class="text-obsidian-300">–</span>
        </div>
      </template>

      <template v-if="hasAnyCostLines">
        <div class="py-0.5 text-right font-bold text-obsidian-900">
          {{ t('total') }}
        </div>
        <div
          v-for="counterpartyType in visibleCounterpartyTypes"
          :key="`total-${counterpartyType}`"
          class="py-0.5 text-right font-bold tabular-nums text-obsidian-900"
          :data-testid="`total-price-${counterpartyType.toLowerCase()}`"
        >
          <span
            v-if="breakdown[counterpartyType].lines.length"
            v-currency="breakdown[counterpartyType].subtotal"
          />
          <span v-else class="font-normal text-obsidian-300">–</span>
        </div>
      </template>

      <div class="py-0.5 text-right text-obsidian-500">{{ t('openBalance') }}</div>
      <div
        v-for="counterpartyType in visibleCounterpartyTypes"
        :key="`balance-${counterpartyType}`"
        class="py-0.5 text-right tabular-nums"
        :class="{
          'font-medium text-red-500': booking.currentBalance[counterpartyType] > EPSILON,
        }"
      >
        <span v-currency="booking.currentBalance[counterpartyType]" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Booking } from '@/entities/bookings/booking.entity';
import { useVisibleBookingCounterparties } from '@/hooks/use-visible-booking-counterparties';
import { EPSILON } from '@/constants';
import {
  CounterpartyType,
  LedgerCostBreakdownBucket,
  type BookingLedgerCostBreakdownDto,
  type LedgerCostBreakdownLineDto,
} from '@carvia/ros-client-types';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const BUCKET_ORDER: Record<LedgerCostBreakdownBucket, number> = {
  [LedgerCostBreakdownBucket.BOOKING_COSTS]: 0,
  [LedgerCostBreakdownBucket.ADDITIONAL_COSTS]: 1,
  [LedgerCostBreakdownBucket.INVOICE_TYPE]: 2,
  [LedgerCostBreakdownBucket.CANCELLATION_REBOOKING_FEES]: 3,
  [LedgerCostBreakdownBucket.OTHER]: 4,
};

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();

const emptyBreakdown: BookingLedgerCostBreakdownDto = {
  AGENCY: { lines: [], subtotal: 0 },
  CUSTOMER: { lines: [], subtotal: 0 },
  OTHER: { lines: [], subtotal: 0 },
};

const breakdown = computed(() => props.booking.ledgerCostBreakdown ?? emptyBreakdown);

const { visibleCounterpartyTypes } = useVisibleBookingCounterparties(
  () => props.booking,
  breakdown,
);

const gridTemplateColumns = computed(
  () => `auto ${visibleCounterpartyTypes.value.map(() => 'minmax(5.5rem, auto)').join(' ')}`,
);

const hasAnyCostLines = computed(() =>
  visibleCounterpartyTypes.value.some(
    (counterpartyType) => breakdown.value[counterpartyType].lines.length > 0,
  ),
);

const getLineKey = (line: LedgerCostBreakdownLineDto): string =>
  line.bucket === LedgerCostBreakdownBucket.INVOICE_TYPE && line.invoiceType
    ? `invoice:${line.invoiceType}`
    : line.bucket;

const getLineSortOrder = (key: string): number => {
  if (key.startsWith('invoice:')) {
    return BUCKET_ORDER[LedgerCostBreakdownBucket.INVOICE_TYPE];
  }
  return BUCKET_ORDER[key as LedgerCostBreakdownBucket] ?? 99;
};

const getLineLabel = (line: LedgerCostBreakdownLineDto): string => {
  switch (line.bucket) {
    case LedgerCostBreakdownBucket.BOOKING_COSTS:
      return t('bookingCosts');
    case LedgerCostBreakdownBucket.ADDITIONAL_COSTS:
      return t('additionalCosts');
    case LedgerCostBreakdownBucket.INVOICE_TYPE:
      return line.invoiceType ? t(`invoiceTypes.${line.invoiceType}`) : t('otherCosts');
    case LedgerCostBreakdownBucket.CANCELLATION_REBOOKING_FEES:
      return t('cancellationRebookingFees');
    case LedgerCostBreakdownBucket.OTHER:
      return t('otherCosts');
    default:
      return t('otherCosts');
  }
};

const costRows = computed(() => {
  const rows = new Map<
    string,
    {
      key: string;
      label: string;
      fromQuotedPrice?: boolean;
      amounts: Partial<Record<CounterpartyType, number>>;
    }
  >();

  for (const counterpartyType of visibleCounterpartyTypes.value) {
    for (const line of breakdown.value[counterpartyType].lines) {
      const key = getLineKey(line);
      const existing = rows.get(key);
      if (existing) {
        existing.amounts[counterpartyType] = line.amount;
        existing.fromQuotedPrice = existing.fromQuotedPrice || line.fromQuotedPrice;
      } else {
        rows.set(key, {
          key,
          label: getLineLabel(line),
          fromQuotedPrice: line.fromQuotedPrice,
          amounts: { [counterpartyType]: line.amount },
        });
      }
    }
  }

  return [...rows.values()].sort((rowA, rowB) => {
    const orderDiff = getLineSortOrder(rowA.key) - getLineSortOrder(rowB.key);
    if (orderDiff !== 0) {
      return orderDiff;
    }
    return rowA.label.localeCompare(rowB.label);
  });
});
</script>

<i18n lang="json">
{
  "en": {
    "bookingCosts": "Booking costs",
    "additionalCosts": "Additional costs",
    "cancellationRebookingFees": "Cancellation / rebooking fees",
    "otherCosts": "Other costs",
    "total": "Total",
    "openBalance": "Open balance",
    "quoted": "(quoted)"
  },
  "de": {
    "bookingCosts": "Buchungskosten",
    "additionalCosts": "Zusatzkosten",
    "cancellationRebookingFees": "Storno- / Umbuchungsgebühren",
    "otherCosts": "Sonstige Kosten",
    "total": "Gesamt",
    "openBalance": "Offener Betrag",
    "quoted": "(angeboten)"
  },
  "es": {
    "bookingCosts": "Costes de reserva",
    "additionalCosts": "Costes adicionales",
    "cancellationRebookingFees": "Gastos de cancelación / cambio",
    "otherCosts": "Otros costes",
    "total": "Total",
    "openBalance": "Saldo pendiente",
    "quoted": "(cotizado)"
  }
}
</i18n>
